import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Duplicates() {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const limit = 100;

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/duplicates/summary"),
        api.get("/leads", { params: { duplicates_only: true, limit, skip, start_date: start || undefined, end_date: end || undefined } }),
      ]);
      setSummary(s.data);
      setLeads(l.data.leads || []);
      setTotal(l.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);

  const Stat = ({ label, value, tid }) => (
    <div className="border border-gray-200 rounded-sm p-4 bg-white" data-testid={tid}>
      <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">{label}</div>
      <div className="font-mono text-2xl font-semibold text-gray-950 mt-2">{(value ?? 0).toLocaleString()}</div>
    </div>
  );

  return (
    <div className="p-6 md:p-10 max-w-[1500px]" data-testid="duplicates-page">
      <div className="mb-8 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Quality</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-gray-950 mt-2">Duplicates</h1>
        <p className="text-gray-600 mt-2">All duplicate leads with the source dates where the same phone first appeared. Reference: <span className="font-mono">{summary?.reference_date || "—"}</span></p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 stagger">
        <Stat label="Last 5d" value={summary?.last_5} tid="dup-summary-5" />
        <Stat label="Last 30d" value={summary?.last_30} tid="dup-summary-30" />
        <Stat label="Last 60d" value={summary?.last_60} tid="dup-summary-60" />
        <Stat label="Last 90d" value={summary?.last_90} tid="dup-summary-90" />
        <Stat label="All Time" value={summary?.all_time} tid="dup-summary-all" />
      </div>

      <Card className="rounded-sm border border-gray-200 shadow-none p-5 bg-white mb-5">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="dup-start-date" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.18em] uppercase text-gray-600">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="dup-end-date" />
          </div>
          <div className="flex items-end">
            <Button onClick={() => { setSkip(0); load(); }} className="w-full rounded-sm h-10 bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="dup-apply">Apply</Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border border-gray-200 shadow-none bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="duplicates-table">
            <thead className="bg-gray-50">
              <tr className="text-[10px] tracking-wider uppercase text-gray-500">
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Source Date</th>
                <th className="text-left px-4 py-3">From File</th>
                <th className="text-left px-4 py-3">Matched Source Dates</th>
                <th className="text-left px-4 py-3">First Seen</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>}
              {!loading && leads.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">No duplicates found.</td></tr>}
              {!loading && leads.map((l) => (
                <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`dup-row-${l.id}`}>
                  <td className="px-4 py-2.5 font-mono">{l.phone}</td>
                  <td className="px-4 py-2.5">{l.first_name} {l.last_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.source_date}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[240px] truncate" title={l.file_name}>{l.file_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-800">{(l.matched_dates || []).join(", ")}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#FF3B30]">{l.oldest_match}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <div data-testid="dup-pagination-info">{leads.length ? `${skip + 1}–${skip + leads.length}` : "0"} of {total.toLocaleString()}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="dup-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="dup-next">Next</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
